import { useState, type ReactNode } from 'react';
import { ErreurApi } from '../lib/api';

/**
 * Mémorise l'état plié/déplié d'une section.
 *
 * Front recharge l'iframe du panneau à chaque changement de conversation :
 * sans mémoire, une section repliée se rouvrirait en permanence et le réglage
 * ne servirait à rien. `localStorage` est propre au navigateur de chacun, ce
 * qui est exactement la portée voulue — c'est une préférence d'affichage, pas
 * une donnée partagée.
 */
const CLE_PLI = 'radar.sections.pliees';

function lirePlis(): Record<string, boolean> {
  try {
    const brut = localStorage.getItem(CLE_PLI);
    return brut ? (JSON.parse(brut) as Record<string, boolean>) : {};
  } catch {
    // Navigation privée, stockage bloqué, JSON abîmé : on repart de zéro
    // plutôt que d'empêcher le panneau de s'afficher.
    return {};
  }
}

function ecrirePli(cle: string, plie: boolean): void {
  try {
    const plis = lirePlis();
    plis[cle] = plie;
    localStorage.setItem(CLE_PLI, JSON.stringify(plis));
  } catch {
    /* sans mémoire, le pli reste valable pour la session en cours */
  }
}

export function Section({
  titre,
  aide,
  action,
  children,
  repliable = false,
  cle,
  ouvertParDefaut = true,
}: {
  titre?: string;
  aide?: string | null;
  action?: ReactNode;
  children: ReactNode;
  /** Rend l'en-tête cliquable et masque le contenu quand il est replié. */
  repliable?: boolean;
  /** Identifiant stable pour se souvenir du pli d'une fois sur l'autre. */
  cle?: string;
  ouvertParDefaut?: boolean;
}) {
  const [ouvert, setOuvert] = useState(() => {
    if (!repliable) return true;
    const memoire = cle ? lirePlis()[cle] : undefined;
    return memoire === undefined ? ouvertParDefaut : !memoire;
  });

  if (!repliable) {
    return (
      <section className="section">
        {(titre || action) && (
          <header className="section-tete">
            {titre && <h2>{titre}</h2>}
            {action}
          </header>
        )}
        {aide && <p className="aide">{aide}</p>}
        {children}
      </section>
    );
  }

  const basculer = () => {
    setOuvert((etait) => {
      if (cle) ecrirePli(cle, etait);
      return !etait;
    });
  };

  const idContenu = `section-${cle ?? titre}`;

  return (
    <section className={`section${ouvert ? '' : ' section--pliee'}`}>
      <header className="section-tete">
        <button
          type="button"
          className="section-bascule"
          onClick={basculer}
          aria-expanded={ouvert}
          aria-controls={idContenu}
        >
          <span className={`chevron${ouvert ? ' chevron--ouvert' : ''}`} aria-hidden="true">
            ▸
          </span>
          {titre && <h2>{titre}</h2>}
        </button>
        {action}
      </header>
      {ouvert && (
        <div id={idContenu} className="section-contenu">
          {aide && <p className="aide">{aide}</p>}
          {children}
        </div>
      )}
    </section>
  );
}

export function Badge({
  ton = 'neutre',
  children,
}: {
  ton?: 'neutre' | 'info' | 'succes' | 'attention' | 'critique';
  children: ReactNode;
}) {
  return <span className={`badge badge--${ton}`}>{children}</span>;
}

export function Squelette({ lignes = 3 }: { lignes?: number }) {
  return (
    <div className="squelette" aria-busy="true" aria-label="Chargement">
      {Array.from({ length: lignes }, (_, index) => (
        <span key={index} className="squelette-ligne" />
      ))}
    </div>
  );
}

export function Vide({ children }: { children: ReactNode }) {
  return <p className="vide">{children}</p>;
}

/** Bandeau d'information, de mise en garde ou d'erreur. */
export function Bandeau({
  ton,
  titre,
  children,
}: {
  ton: 'info' | 'attention' | 'critique' | 'succes';
  titre?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`bandeau bandeau--${ton}`} role={ton === 'critique' ? 'alert' : undefined}>
      {titre && <strong>{titre}</strong>}
      {children && <span>{children}</span>}
    </div>
  );
}

/**
 * Traduction d'une erreur d'API en message utilisable.
 *
 * Le 409 est le cas important : au démarrage, une bonne partie des clients n'a
 * pas encore de bilan. C'est un état d'attente normal, pas une panne, et il ne
 * doit jamais s'afficher comme une erreur technique.
 */
export function ErreurAffichee({
  erreur,
  onReessayer,
}: {
  erreur: ErreurApi;
  onReessayer?: () => void;
}) {
  if (erreur.bilanPasEncorePret) {
    return (
      <Bandeau ton="info" titre="Bilan pas encore disponible">
        {erreur.indice ?? 'Le pipeline ne l’a pas encore produit. Réessayez plus tard.'}
      </Bandeau>
    );
  }

  if (erreur.conversationInconnue) {
    return (
      <Bandeau ton="info" titre="Conversation hors référentiel">
        {erreur.indice ?? 'Cette conversation n’a pas été parcourue par le pipeline.'}
      </Bandeau>
    );
  }

  const titre = erreur.jetonInvalide
    ? 'Jeton refusé'
    : erreur.statut === 429
      ? 'Trop de requêtes'
      : erreur.statut === 503
        ? 'Pipeline indisponible'
        : 'Le contexte n’a pas pu être chargé';

  return (
    <div className="erreur-bloc">
      <Bandeau ton="critique" titre={titre}>
        {erreur.message}
        {erreur.indice ? ` ${erreur.indice}` : ''}
        {erreur.retryApres ? ` Réessayez dans ${erreur.retryApres} s.` : ''}
      </Bandeau>
      {onReessayer && !erreur.jetonInvalide && (
        <button type="button" className="bouton" onClick={onReessayer}>
          Réessayer
        </button>
      )}
    </div>
  );
}

/** Jauge du score de reprise (0-100). */
export function Score({ valeur, libelle = 'Score de reprise' }: { valeur: number; libelle?: string }) {
  const borne = Math.max(0, Math.min(100, valeur));
  const ton = borne >= 70 ? 'haut' : borne >= 40 ? 'moyen' : 'bas';
  return (
    <div className="score" title={`${libelle} : ${borne} sur 100`}>
      <span className="score-libelle">{libelle}</span>
      <span className="score-piste">
        <span className={`score-jauge score-jauge--${ton}`} style={{ width: `${borne}%` }} />
      </span>
      <span className="score-valeur">{borne}</span>
    </div>
  );
}
